import licensedWidgetPackage from '@dx-private/dxchart5-react-widget/package.json'
import dxchartReactPackage from '@dx-private/dxchart5-react/package.json'
import mockProvidersPackage from '@dx-private/dxchart5-react-mock-providers/package.json'
import dxchartsLitePackage from '@devexperts/dxcharts-lite/package.json'
import dxstudiesPackage from '@dx-private/dxstudies/package.json'
import type { ChartReactWidgetProps } from '@dx-private/dxchart5-react-widget/dist/widget.config'
import { licensedWidgetUpdateManifest } from './licensed-widget-update-manifest'

const LICENSED_WIDGET_RUNTIME_URL = `${process.env.PUBLIC_URL || ''}/vendor/dxchart5-react-widget/dist/index.js`
const LICENSED_WIDGET_RUNTIME_SCRIPT_ID = 'utc-licensed-widget-runtime'

export type LicensedWidgetDiagnosticCategory =
  | 'host-packaging'
  | 'internal-chart-compatibility'

export interface LicensedWidgetDiagnostic {
  readonly category: LicensedWidgetDiagnosticCategory
  readonly code: string
  readonly message: string
  readonly feature?: string
}

interface PackageMetadata {
  readonly name: string
  readonly version: string
  readonly main?: string
  readonly dependencies?: Record<string, string>
}

interface DxchartsLiteMetadata {
  readonly name: string
  readonly version: string
}

interface SupportedVersionTuple {
  readonly widgetRuntime: string
  readonly providerContract: string
  readonly chartCore: string
}

interface ProvenanceExpectation {
  readonly label: string
  readonly actual: string
  readonly expected: string
}

interface WindowDXChartRuntime {
  readonly createWidget?: (
    container: HTMLElement,
    props: ChartReactWidgetProps,
  ) => Promise<{ destroy: () => void }>
  readonly widget?: {
    readonly createWidget?: (
      container: HTMLElement,
      props: ChartReactWidgetProps,
    ) => Promise<{ destroy: () => void }>
  }
}

declare global {
  interface Window {
    DXChart?: WindowDXChartRuntime
  }
}

// Task 4.1: Explicit supported version combinations
// Each tuple declares a verified compatible runtime, provider-contract, and chart-core set
// Only combinations tested and confirmed to work should be added to this list
const supportedVersionTuples: readonly SupportedVersionTuple[] = [
  {
    widgetRuntime: '5.19.2',
    providerContract: '5.18.0-rc3',
    chartCore: '2.7.32', // Current staged bundle
  },
  {
    widgetRuntime: '5.18.0',
    providerContract: '5.18.0-rc3',
    chartCore: '2.7.26', // Verified: known-good 5.18 drop
  },
] as const

const packageProvenanceExpectations: readonly ProvenanceExpectation[] = [
  {
    label: `${licensedWidgetPackage.name} version`,
    actual: licensedWidgetPackage.version,
    expected: licensedWidgetUpdateManifest.widgetPackage.version,
  },
  {
    label: `${licensedWidgetPackage.name} main`,
    actual: (licensedWidgetPackage as PackageMetadata).main ?? 'missing',
    expected: licensedWidgetUpdateManifest.widgetPackage.main,
  },
  {
    label: `${licensedWidgetPackage.name} -> @devexperts/dxcharts-lite`,
    actual:
      (licensedWidgetPackage as PackageMetadata).dependencies?.['@devexperts/dxcharts-lite'] ??
      'missing',
    expected: licensedWidgetUpdateManifest.widgetPackage.dxchartsLiteVersion,
  },
  {
    label: `${mockProvidersPackage.name} version`,
    actual: mockProvidersPackage.version,
    expected: licensedWidgetUpdateManifest.mockProvidersPackage.version,
  },
  {
    label: `${mockProvidersPackage.name} -> @devexperts/dxcharts-lite`,
    actual:
      (mockProvidersPackage as PackageMetadata).dependencies?.['@devexperts/dxcharts-lite'] ??
      'missing',
    expected: licensedWidgetUpdateManifest.mockProvidersPackage.dxchartsLiteVersion,
  },
  {
    label: `${dxstudiesPackage.name} version`,
    actual: dxstudiesPackage.version,
    expected: licensedWidgetUpdateManifest.dxstudiesPackage.version,
  },
] as const

// Task 4.1-4.2: Check if current package combination matches a supported tuple
function isSupportedVersionTuple(
  widgetRuntime: string,
  providerContract: string,
  chartCore: string
): boolean {
  return supportedVersionTuples.some(
    (tuple) =>
      tuple.widgetRuntime === widgetRuntime &&
      tuple.providerContract === providerContract &&
      tuple.chartCore === chartCore
  )
}

function buildVersionMismatchDiagnostics(): LicensedWidgetDiagnostic[] {
  // Task 4.2: Validate supported package tuple
  if (!isSupportedVersionTuple(
    licensedWidgetPackage.version,
    dxchartReactPackage.version,
    dxchartsLitePackage.version
  )) {
    return [
      {
        category: 'host-packaging',
        code: 'unsupported-version-combination',
        feature: 'widget-compatibility-tuple',
        message: `Unsupported widget package combination: @dx-private/dxchart5-react-widget=${licensedWidgetPackage.version}, @dx-private/dxchart5-react=${dxchartReactPackage.version}, @devexperts/dxcharts-lite=${dxchartsLitePackage.version}. Supported combinations: ${supportedVersionTuples.map((t) => `(runtime=${t.widgetRuntime}, provider-contract=${t.providerContract}, chart-core=${t.chartCore})`).join('; ')}. ${licensedWidgetUpdateManifest.rollbackTarget}`,
      },
    ]
  }

  return []
}

function buildPackageProvenanceDiagnostics(): LicensedWidgetDiagnostic[] {
  return packageProvenanceExpectations.flatMap((expectation) => {
    if (expectation.actual === expectation.expected) {
      return []
    }

    return [
      {
        category: 'host-packaging',
        code: 'staged-package-provenance-drift',
        feature: expectation.label,
        message: `Licensed widget vendor build ${licensedWidgetUpdateManifest.vendorBuildLabel} expects ${expectation.label}=${expectation.expected}, but found ${expectation.actual}. ${licensedWidgetUpdateManifest.rollbackTarget}`,
      },
    ] satisfies readonly LicensedWidgetDiagnostic[]
  })
}

export function formatLicensedWidgetDiagnostics(
  diagnostics: readonly LicensedWidgetDiagnostic[],
): string {
  return diagnostics.map((diagnostic) => diagnostic.message).join(' ')
}

export class LicensedWidgetStartupError extends Error {
  readonly diagnostics: readonly LicensedWidgetDiagnostic[]

  constructor(message: string, diagnostics: readonly LicensedWidgetDiagnostic[]) {
    super(message)
    this.name = 'LicensedWidgetStartupError'
    this.diagnostics = diagnostics
  }
}

function buildStartupValidationDiagnostics(): LicensedWidgetDiagnostic[] {
  const diagnostics: LicensedWidgetDiagnostic[] = []

  diagnostics.push(...buildVersionMismatchDiagnostics())
  diagnostics.push(...buildPackageProvenanceDiagnostics())
  return diagnostics
}

function resolveLicensedWidgetRuntime(): WindowDXChartRuntime | null {
  const runtime = window.DXChart
  if (!runtime) {
    return null
  }

  if (typeof runtime.createWidget === 'function') {
    return runtime
  }

  if (typeof runtime.widget?.createWidget === 'function') {
    return {
      createWidget: runtime.widget.createWidget,
      widget: runtime.widget,
    }
  }

  return null
}

let runtimePromise: Promise<WindowDXChartRuntime> | null = null
let runtimeProvenancePromise: Promise<void> | null = null

async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
  const digest = await window.crypto.subtle.digest('SHA-256', buffer)
  return Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, '0')).join(
    '',
  )
}

async function validateRuntimeBuildProvenance(): Promise<void> {
  if (runtimeProvenancePromise) {
    return runtimeProvenancePromise
  }

  runtimeProvenancePromise = (async () => {
    if (!window.crypto?.subtle) {
      throw new LicensedWidgetStartupError(
        'Licensed widget provenance checks require Web Crypto support.',
        [
          {
            category: 'host-packaging',
            code: 'runtime-provenance-unavailable',
            feature: 'runtime-assets',
            message: `Licensed widget vendor build ${licensedWidgetUpdateManifest.vendorBuildLabel} could not verify runtime asset provenance because Web Crypto is unavailable. ${licensedWidgetUpdateManifest.rollbackTarget}`,
          },
        ],
      )
    }

    let response: Response
    try {
      response = await fetch(LICENSED_WIDGET_RUNTIME_URL, {
        cache: 'no-store',
      })
    } catch (error) {
      throw new LicensedWidgetStartupError(
        `Licensed widget runtime failed to load from ${LICENSED_WIDGET_RUNTIME_URL}.`,
        [
          {
            category: 'host-packaging',
            code: 'runtime-script-load-failed',
            feature: 'runtime-assets',
            message: `Licensed widget runtime assets could not be fetched from ${LICENSED_WIDGET_RUNTIME_URL}. ${licensedWidgetUpdateManifest.rollbackTarget}`,
          },
        ],
      )
    }

    if (!response.ok) {
      throw new LicensedWidgetStartupError(
        `Licensed widget runtime failed to load from ${LICENSED_WIDGET_RUNTIME_URL}.`,
        [
          {
            category: 'host-packaging',
            code: 'runtime-script-load-failed',
            feature: 'runtime-assets',
            message: `Licensed widget runtime assets returned HTTP ${response.status} from ${LICENSED_WIDGET_RUNTIME_URL}. ${licensedWidgetUpdateManifest.rollbackTarget}`,
          },
        ],
      )
    }

    const actualHash = await sha256Hex(await response.arrayBuffer())
    if (actualHash !== licensedWidgetUpdateManifest.runtimeAssets.indexJsSha256) {
      throw new LicensedWidgetStartupError(
        'Licensed widget runtime provenance does not match the staged vendor build.',
        [
          {
            category: 'host-packaging',
            code: 'runtime-build-provenance-drift',
            feature: 'runtime-assets',
            message: `Licensed widget vendor build ${licensedWidgetUpdateManifest.vendorBuildLabel} expects runtime index.js sha256=${licensedWidgetUpdateManifest.runtimeAssets.indexJsSha256}, but ${LICENSED_WIDGET_RUNTIME_URL} resolved to ${actualHash}. ${licensedWidgetUpdateManifest.rollbackTarget}`,
          },
        ],
      )
    }
  })().catch((error) => {
    runtimeProvenancePromise = null
    throw error
  })

  return runtimeProvenancePromise
}

function loadRuntimeScript(): Promise<void> {
  const existing = document.getElementById(
    LICENSED_WIDGET_RUNTIME_SCRIPT_ID,
  ) as HTMLScriptElement | null

  if (existing) {
    return new Promise((resolve, reject) => {
      if (existing.dataset.loaded === 'true') {
        resolve()
        return
      }

      const handleLoad = () => {
        existing.dataset.loaded = 'true'
        resolve()
      }
      const handleError = () => {
        reject(
          new LicensedWidgetStartupError(
            `Licensed widget runtime failed to load from ${LICENSED_WIDGET_RUNTIME_URL}.`,
            [
              {
                category: 'host-packaging',
                code: 'runtime-script-load-failed',
                feature: 'runtime-assets',
                message: `Licensed widget runtime assets could not be loaded from ${LICENSED_WIDGET_RUNTIME_URL}.`,
              },
            ],
          ),
        )
      }

      existing.addEventListener('load', handleLoad, { once: true })
      existing.addEventListener('error', handleError, { once: true })
    })
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.id = LICENSED_WIDGET_RUNTIME_SCRIPT_ID
    script.src = LICENSED_WIDGET_RUNTIME_URL
    script.async = true
    script.onload = () => {
      script.dataset.loaded = 'true'
      resolve()
    }
    script.onerror = () => {
      reject(
        new LicensedWidgetStartupError(
          `Licensed widget runtime failed to load from ${LICENSED_WIDGET_RUNTIME_URL}.`,
          [
            {
              category: 'host-packaging',
              code: 'runtime-script-load-failed',
              feature: 'runtime-assets',
              message: `Licensed widget runtime assets could not be loaded from ${LICENSED_WIDGET_RUNTIME_URL}.`,
            },
          ],
        ),
      )
    }
    document.head.appendChild(script)
  })
}

export function validateLicensedWidgetStartup(): LicensedWidgetDiagnostic[] {
  return buildStartupValidationDiagnostics()
}

export async function loadLicensedWidgetRuntime(): Promise<WindowDXChartRuntime> {
  const versionDiagnostics = validateLicensedWidgetStartup()
  if (versionDiagnostics.length > 0) {
    throw new LicensedWidgetStartupError(
      formatLicensedWidgetDiagnostics(versionDiagnostics),
      versionDiagnostics,
    )
  }

  await validateRuntimeBuildProvenance()

  const existingRuntime = resolveLicensedWidgetRuntime()
  if (existingRuntime) {
    return existingRuntime
  }

  if (!runtimePromise) {
    runtimePromise = (async () => {
      await loadRuntimeScript()

      const runtime = resolveLicensedWidgetRuntime()
      if (!runtime?.createWidget) {
        throw new LicensedWidgetStartupError(
          `Licensed widget runtime loaded from ${LICENSED_WIDGET_RUNTIME_URL}, but createWidget is unavailable.`,
          [
            {
              category: 'host-packaging',
              code: 'missing-create-widget',
              feature: 'runtime-api',
              message: 'Licensed widget runtime did not expose createWidget(container, props) after loading.',
            },
          ],
        )
      }

      return runtime
    })().catch((error) => {
      runtimePromise = null
      throw error
    })
  }

  return runtimePromise
}

export const licensedWidgetMetadata = {
  runtimeUrl: LICENSED_WIDGET_RUNTIME_URL,
  packageName: licensedWidgetPackage.name,
  packageVersion: licensedWidgetPackage.version,
  chartPackageVersion: (dxchartReactPackage as PackageMetadata).version,
  chartLiteVersion: (dxchartsLitePackage as DxchartsLiteMetadata).version,
  packageMain: (licensedWidgetPackage as PackageMetadata).main ?? '',
  vendorBuildLabel: licensedWidgetUpdateManifest.vendorBuildLabel,
  runtimeTreeSha256: licensedWidgetUpdateManifest.runtimeAssets.treeSha256,
  runtimeIndexSha256: licensedWidgetUpdateManifest.runtimeAssets.indexJsSha256,
  widgetPackageTreeSha256: licensedWidgetUpdateManifest.widgetPackage.treeSha256,
  rollbackTarget: licensedWidgetUpdateManifest.rollbackTarget,
} as const

